import { Pencil, Trash2, Smartphone, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";

const EMPTY =
  "https://images.unsplash.com/photo-1670938904389-5a7b7ac1394d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxlbXB0eSUyMHBsYXRlfGVufDB8fHx8MTc4NjYyMDgxNnww&ixlib=rb-4.1.0&q=85";

const TYPE_COLORS = {
  breakfast: "bg-[#EAE5D9] text-deep-olive",
  lunch: "bg-forest/10 text-forest",
  dinner: "bg-terracotta/15 text-terracotta",
  snack: "bg-ochre/20 text-[#8A6220]",
};

const time = (iso) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const day = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export const MealFeed = ({ meals, onEdit, onDelete }) => {
  return (
    <div
      className="bg-surface rounded-xl border border-[#E8E3D9] p-6 hover:shadow-md transition-shadow duration-200"
      data-testid="meal-feed"
    >
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-moss">Meal log feed</p>
      <h3 className="mt-1 font-heading text-xl sm:text-2xl font-medium tracking-tight text-deep-olive">
        {meals.length} entries
      </h3>

      {meals.length === 0 ? (
        <div className="mt-6 overflow-hidden rounded-lg border border-[#E8E3D9]" data-testid="feed-empty-state">
          <img src={EMPTY} alt="Empty plate" className="h-40 w-full object-cover" />
          <p className="px-5 py-4 text-sm text-stone-grey">
            No meals in this range yet. Log one from the web or your phone.
          </p>
        </div>
      ) : (
        <ul className="mt-5 max-h-[620px] space-y-3 overflow-y-auto pr-1">
          {meals.map((m, i) => (
            <li
              key={m.meal_id}
              data-testid={`meal-item-${m.meal_id}`}
              className="group rounded-lg border border-[#E8E3D9] bg-sand p-4 animate-rise-in hover:-translate-y-1 hover:border-[#D5CEBD] transition-[transform,border-color] duration-200"
              style={{ animationDelay: `${Math.min(i, 8) * 45}ms` }}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${TYPE_COLORS[m.meal_type]}`}
                >
                  {m.meal_type}
                </span>
                <span className="ml-auto text-xs text-stone-grey whitespace-nowrap">
                  {day(m.consumed_at)} · {time(m.consumed_at)}
                </span>
                {m.source === "mobile" ? (
                  <Smartphone className="h-3.5 w-3.5 shrink-0 text-[#9CA392]" />
                ) : (
                  <Monitor className="h-3.5 w-3.5 shrink-0 text-[#9CA392]" />
                )}
              </div>

              <p className="mt-2.5 font-heading font-medium text-deep-olive">{m.name}</p>

              {(m.photo_url || m.after_photo_url) && (
                <div className="mt-2.5 grid grid-cols-2 gap-2" data-testid={`meal-photos-${m.meal_id}`}>
                  {[
                    ["Before", m.photo_url],
                    ["After", m.after_photo_url],
                  ]
                    .filter(([, url]) => url)
                    .map(([label, url]) => (
                      <div key={label} className="relative overflow-hidden rounded-md">
                        <img src={url} alt={`${m.name} ${label}`} className="h-20 w-full object-cover" />
                        <span className="absolute bottom-1 left-1 rounded bg-deep-olive/70 px-1.5 py-0.5 text-[10px] font-medium text-sand">
                          {label}
                        </span>
                      </div>
                    ))}
                </div>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone-grey">
                <span className="font-medium text-deep-olive">
                  {Math.round(m.consumed.calories)} kcal
                </span>
                <span>P {Math.round(m.consumed.protein)}g</span>
                <span>C {Math.round(m.consumed.carbs)}g</span>
                <span>F {Math.round(m.consumed.fat)}g</span>
              </div>

              <div className="mt-3 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#EAE5D9]">
                  <div
                    className="h-full rounded-full bg-moss"
                    style={{ width: `${Math.max(0, m.portion_before - m.portion_after)}%` }}
                  />
                </div>
                <span className="text-xs text-stone-grey whitespace-nowrap">
                  {Math.max(0, m.portion_before - m.portion_after)}% eaten
                </span>
                <Button
                  data-testid={`edit-meal-${m.meal_id}`}
                  variant="ghost"
                  onClick={() => onEdit(m)}
                  className="h-8 w-8 rounded-full p-0 text-stone-grey hover:bg-surface-hover hover:text-forest transition-colors duration-200"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  data-testid={`delete-meal-${m.meal_id}`}
                  variant="ghost"
                  onClick={() => onDelete(m)}
                  className="h-8 w-8 rounded-full p-0 text-stone-grey hover:bg-terracotta/15 hover:text-terracotta transition-colors duration-200"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
